import type { Size } from './canvasMath';
import { applyStrokesToContext, strokesAreEmpty, type Stroke, type StrokeDrawContext } from './maskMath';

/**
 * Sketch rendering reuses the brush capture infrastructure from `maskMath`
 * (Stroke / StrokeDrawContext / screen ↔ image coord helpers) but emits a
 * different visual: dark strokes on an opaque white background, sized to
 * the configured sketch tablet rather than a source image.
 *
 * For SketchToImage, the sketch IS the source image — the panel uploads
 * the rendered dataURL as `sourceImageBase64` and the BE feeds it as a
 * ControlNet input (B15 wired `ControlNetType="sketch"`).
 */

const DEFAULT_SKETCH_STROKE_COLOR = 'rgba(20, 20, 20, 1)'; // near-black; visible against white
const DEFAULT_SKETCH_BG_COLOR = 'rgba(255, 255, 255, 1)';

export interface SketchPalette {
  strokeColor: string;
  backgroundColor: string;
}

export const DEFAULT_SKETCH_PALETTE: SketchPalette = {
  strokeColor: DEFAULT_SKETCH_STROKE_COLOR,
  backgroundColor: DEFAULT_SKETCH_BG_COLOR,
};

/**
 * Paint a filled background then `strokes` onto a 2D drawing context that's
 * been sized to the sketch tablet's pixel dimensions. Returns void; caller
 * owns the context.
 *
 * Differs from `applyStrokesToContext` (mask) in two ways:
 * (1) the background is opaque white rather than transparent
 * (2) stroke color is dark rather than opaque white
 *
 * Extracted so the draw-call sequence is testable without a real
 * CanvasRenderingContext2D.
 */
export function applySketchToContext(
  strokes: Stroke[],
  ctx: StrokeDrawContext,
  size: Size,
  palette: SketchPalette = DEFAULT_SKETCH_PALETTE,
): void {
  ctx.clearRect(0, 0, size.width, size.height);
  ctx.globalCompositeOperation = 'source-over';
  ctx.fillStyle = palette.backgroundColor;
  ctx.strokeStyle = palette.backgroundColor;
  ctx.lineWidth = 0;
  // Paint white background as a filled rect via a degenerate stroke path.
  // We re-use applyStrokesToContext for the stroke pass; clearRect + a
  // single big background draw is the cheapest path that's still testable.
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(size.width, 0);
  ctx.lineTo(size.width, size.height);
  ctx.lineTo(0, size.height);
  ctx.fill();

  // Now overlay strokes with the dark color. Re-use the mask draw sequence
  // but swap the fillStyle / strokeStyle. applyStrokesToContext sets its own
  // fillStyle to opaque white internally — we run a separate pass here to
  // preserve our dark stroke color.
  ctx.fillStyle = palette.strokeColor;
  ctx.strokeStyle = palette.strokeColor;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';

  for (const stroke of strokes) {
    const { points, brushSize } = stroke;
    if (points.length < 2) continue;
    if (points.length === 2) {
      ctx.beginPath();
      ctx.arc(points[0], points[1], brushSize / 2, 0, Math.PI * 2);
      ctx.fill();
      continue;
    }
    ctx.lineWidth = brushSize;
    ctx.beginPath();
    ctx.moveTo(points[0], points[1]);
    for (let i = 2; i < points.length; i += 2) {
      ctx.lineTo(points[i], points[i + 1]);
    }
    ctx.stroke();
  }
}

/**
 * Render `strokes` to a data URL sized to `size`. Always returns a dataURL
 * for a non-empty sketch — even an empty stroke set produces a white-only
 * sketch (caller decides if that's useful). Returns null only when the
 * host lacks a 2D context (jsdom) or the size is non-positive.
 *
 * Unlike `renderStrokesToDataUrl` (mask), this returns null on empty
 * strokes intentionally — a blank-white sketch isn't a meaningful input
 * to SketchToImage. The panel's submit handler treats null as "draw
 * something first".
 */
export function renderSketchToDataUrl(
  strokes: Stroke[],
  size: Size,
  palette: SketchPalette = DEFAULT_SKETCH_PALETTE,
): string | null {
  if (size.width <= 0 || size.height <= 0) return null;
  if (strokesAreEmpty(strokes)) return null;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(size.width);
  canvas.height = Math.round(size.height);
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  applySketchToContext(strokes, ctx as unknown as StrokeDrawContext, size, palette);
  return canvas.toDataURL('image/png');
}

// Re-export applyStrokesToContext via a wrapper so callers that just want
// to render an arbitrary stroke set with a custom palette can do so without
// reimplementing the draw loop. Currently unused but kept for symmetry.
export { applyStrokesToContext };
